/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2023, Zumper
 * @description PinMarkerListingPreview
 */

import { selectFilters } from '@zcode-ui/filters'
import { useRouteProps } from '@zcode-ui/hook'
import { ListingCardSizes } from '@zcode-ui/web-zumper-features'
import { getUser, selectIsBot, selectIsMobile } from '@zcode-ui/web-zumper-global-state'
import { PIN_LISTING_CARD_CLICK, event } from '@zcode-ui/web-zumper-integrations'
import * as Optimizely from '@zcode/nr/es/shared/packages/optimizely-middleware'
import { PIN_LISTING_CARD_CLICK as OPTLY_PIN_LISTING_CARD_CLICK } from '@zcode/nr/es/shared/thirdParty/optimizely/constants'
import { AnalyticsC, isGalleryDirect } from '@zcode/nr-utils'
import type { Listable, Pin, RawPinInventorySearch } from '@zcode/state-type'
import React, { type KeyboardEvent, type MouseEvent } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { usePinMarkerContext } from '../../../../pages/PinMarkerContext'
import type { MixpanelMaplistClickType } from '../../../../types'

export const PinMarkerListingPreview = ({
  isGroup,
  listable,
  mixpanelMaplistClick,
  onClose,
}: {
  isGroup?: boolean
  listable: Listable | Pin<RawPinInventorySearch>
  onClose: () => void
  mixpanelMaplistClick: ({
    buttonClicked,
    entity,
    pageName,
    section,
  }: MixpanelMaplistClickType) => void
}) => {
  const { ListingCard, openFullDetailTab } = usePinMarkerContext()
  const dispatch = useDispatch()
  const isMobile = useSelector(selectIsMobile)
  const filters = useSelector(selectFilters)
  const user = useSelector(getUser)
  const isBot = useSelector(selectIsBot)
  const { location } = useRouteProps()
  const isGalleryDirectUx = isGalleryDirect({ location, isBot })
  const isGalleryURL = listable.hasImage && isGalleryDirectUx

  const onClick = (e: KeyboardEvent<HTMLElement> | MouseEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()
    mixpanelMaplistClick({
      buttonClicked: 'View Detail',
      section: 'Pin Listing Card',
      entity: listable,
    })

    dispatch(event({ name: PIN_LISTING_CARD_CLICK, props: { entity: listable }, filters, user }))
    dispatch(Optimizely.track({ eventKey: OPTLY_PIN_LISTING_CARD_CLICK }))

    dispatch(openFullDetailTab({ entity: listable, filters, isSameTab: isMobile, isGalleryURL }))
  }

  const handleClose = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    onClose()
  }

  if (isMobile) {
    return (
      <ListingCard
        entity={listable}
        onClick={onClick}
        onClose={isGroup ? undefined : handleClose}
        size={ListingCardSizes.SM}
        withBorder={!isGroup}
      />
    )
  }

  return (
    <ListingCard
      analyticChannel={AnalyticsC.LIST}
      entity={listable}
      forceMobile={isGroup}
      onClick={onClick}
      onClose={isGroup ? undefined : handleClose}
      onDetailClick={onClick}
      origin="map"
      shouldPreloadImage
      showPropertyBadge={false}
      size={ListingCardSizes.MD}
      withBorder={!isGroup}
      withBoxShadow={!isGroup}
    />
  )
}
